import React, { useState } from 'react';

import { clearURLParams, setURLParams } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import SearchInput from 'components/SearchInput';
import SelectRedesign from 'components/Select/SelectRedesign';
import { kindOptions, usageOptions } from 'constants/entities';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import pluralize from 'pluralize';
import { useDispatch, useSelector } from 'react-redux';
import { getPrompts, getPromptsLoading, getPromptsTotalCount } from 'selectors/entities';
import { getNavParamsByResource } from 'selectors/nav';
import EditPromptModal from './EditPromptModal';

const PromptHeader: React.FC = () => {
  const dispatch = useDispatch();

  const navParams = useSelector(getNavParamsByResource(resourceQueryParamName.prompts));

  const kind = navParams.kind as string;
  const inUse = navParams.in_use as string;
  const offset = parseInt(navParams.offset as string, 10);

  const loading = useSelector(getPromptsLoading);
  const prompts = useSelector(getPrompts);
  const totalCount = useSelector(getPromptsTotalCount);

  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);

  const toggleCreateModal = (): void => setIsCreatingPrompt(!isCreatingPrompt);

  const handleCreateNew = (): void => {
    setIsCreatingPrompt(true);
  };

  const handleSelectKind = (value: string): void => {
    dispatch(setURLParams({ prompts__kind: value }));
  };

  const handleSelectUsage = (value: string): void => {
    dispatch(setURLParams({ prompts__in_use: value }));
  };

  const handleClearFilters = (): void => {
    dispatch(clearURLParams());
  };

  const isFiltering = kind || inUse || navParams.name;

  return (
    <>
      <div className="flex flex-col gap-6 justify-between px-6 w-full">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center">
            <h2 className="text-heading-2">Prompts</h2>
            {loading && <LoadingIndicator className="ml-2" size="5" />}
          </div>
          <button
            onClick={handleCreateNew}
            type="button"
            data-testid="create-report-button"
            className="button button--primary h-8 w-37 justify-center"
          >
            New Prompt
          </button>
        </div>
        <div className="flex flex-row justify-between gap-2 h-8">
          <div className="flex gap-2">
            <SearchInput
              className="max-w-60"
              resource={resourceQueryParamName.prompts}
              field="name"
              placeholder="Search prompts"
            />
            <div className="flex-shrink-0 w-32">
              <SelectRedesign
                onChange={handleSelectKind}
                options={kindOptions}
                value={kind}
                placeholder="Type"
                dataTestid="type-select"
              />
            </div>
            <div className="flex-shrink-0 w-32">
              <SelectRedesign
                onChange={handleSelectUsage}
                options={usageOptions}
                value={inUse}
                placeholder="Usage"
                dataTestid="usage-select"
              />
            </div>
            <button
              type="button"
              className={`self-end underline ${
                isFiltering ? '' : 'cursor-default text-litlingo-gray-3'
              } focus:outline-none`}
              disabled={!isFiltering}
              onClick={handleClearFilters}
            >
              Clear All
            </button>
          </div>
          <div className="self-end">
            {loading ? (
              <LoadingIndicator className="ml-2" size="5" />
            ) : (
              <>
                <span className="font-bold">
                  {` ${prompts.length === 0 ? 0 : offset + 1}-${offset + prompts.length}`}
                </span>
                {` of ${totalCount === 10000 ? '+10,000' : pluralize('prompt', totalCount, true)}`}
              </>
            )}
          </div>
        </div>
      </div>

      {isCreatingPrompt && (
        <EditPromptModal
          isCreating
          isModalOpen={isCreatingPrompt}
          toggleModalOpen={toggleCreateModal}
          redirect
        />
      )}
    </>
  );
};

export default PromptHeader;
